import { sortByKey } from "@notemeal/utils-sort";
import { AddMealOptionDocument, AddMealOptionMutation, AddMealOptionMutationVariables, EditFullMealTemplateFragment, EditFullMealTemplateFragmentDoc, FullMealOptionFragment, FullMealOptionPendingFragment, FullMealTemplateFragment, MutationAddMealOptionLocalArgs, MutationRemoveMealOptionLocalArgs, MutationRemoveMealOptionPendingLocalArgs, MutationSelectMealOptionArgs, MutationShareMealOptionPendingLocalArgs, RemoveMealOptionDocument, RemoveMealOptionMutation, RemoveMealOptionMutationVariables, RemoveMealOptionPendingDocument, RemoveMealOptionPendingMutation, RemoveMealOptionPendingMutationVariables, ShareMealOptionPendingDocument, ShareMealOptionPendingMutation, ShareMealOptionPendingMutationVariables } from "apps/web/src/types";
import { getNextPosition } from "../../utils/getNextPosition";
import { createMealOption, initEditMealOptionFragment } from "./MealOption";
import { ResolverContext } from "./types";
import { makeFragmentFuncsWithInit } from "./utils";
import { initEditMealOptionPendingFragment } from "./MealOptionPending";

const mealOptionPreviewsToSelectedId = (
  mealOptions: readonly FullMealOptionFragment[],
  pendingMealOptions: readonly FullMealOptionPendingFragment[]
): string | null => {
  const sortedMealOptions = sortByKey(mealOptions, "position");
  return pendingMealOptions.length ? pendingMealOptions[0].id : sortedMealOptions.length ? sortedMealOptions[0].id : null;
};

export const initEditMealTemplateFragment = ({
  mealOptions,
  pendingMealOptions,
  ...mealTemplate
}: FullMealTemplateFragment): EditFullMealTemplateFragment => {
  return {
    ...mealTemplate,
    selectedMealOptionId: mealOptionPreviewsToSelectedId(mealOptions, pendingMealOptions),
    isAutosaving: false,
    newMealOptionIds: [],
    mealOptions: mealOptions.map(initEditMealOptionFragment),
    pendingMealOptions: pendingMealOptions.map(initEditMealOptionPendingFragment),
  };
};

export const macroEditFragmentFuncs = makeFragmentFuncsWithInit(
  "MacroMealTemplate",
  "EditFullMealTemplate",
  EditFullMealTemplateFragmentDoc,
  initEditMealTemplateFragment
);

export const exchangeEditFragmentFuncs = makeFragmentFuncsWithInit(
  "ExchangeMealTemplate",
  "EditFullMealTemplate",
  EditFullMealTemplateFragmentDoc,
  initEditMealTemplateFragment
);

const getEditFragmentFuncs = (type: string) => {
  if (type === "MacroMealTemplate") return macroEditFragmentFuncs;
  if (type === "ExchangeMealTemplate") return exchangeEditFragmentFuncs;
  throw Error("Invalid type for getEditFragmentFuncs");
};

export const mutationResolverMap = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectMealOption: (src: any, { mealTemplateId, mealOptionId, type }: MutationSelectMealOptionArgs, context: ResolverContext) => {
    const { getFragment, writeFragment } = getEditFragmentFuncs(type);
    const editFragment = getFragment(mealTemplateId, context);
    writeFragment(
      {
        ...editFragment,
        selectedMealOptionId: mealOptionId,
      },
      context
    );
  },

  shareMealOptionPendingLocal: async (
    src: any,
    { mealTemplateId, mealOptionPendingId }: MutationShareMealOptionPendingLocalArgs,
    context: ResolverContext
  ) => {
    const { getFragment, writeFragment } = getEditFragmentFuncs("MacroMealTemplate");
    const { client } = context;
    const { pendingMealOptions, mealOptions, newMealOptionIds, selectedMealOptionId, ...editFragment } = getFragment(
      mealTemplateId,
      context
    );
    const pendingToShare = pendingMealOptions.find(pendingMealOption => pendingMealOption.id === mealOptionPendingId);

    if (!pendingToShare) {
      throw new Error(`unable to share pending meal option id: ${mealOptionPendingId}`);
    }

    const previousState = {
      ...editFragment,
      newMealOptionIds,
      mealOptions,
      pendingMealOptions,
      selectedMealOptionId,
    };

    // Optimistically promote in place: pending → shared MealOption at the front of the shared list.
    // Match server: template options are 0-indexed, so front is min(position), not always 1.
    const sharedPosition = mealOptions.length === 0 ? 1 : Math.min(...mealOptions.map(mealOption => mealOption.position));
    const optimisticMealOption = {
      ...createMealOption(sharedPosition),
      name: pendingToShare.name,
      note: pendingToShare.note,
      servingAmounts: pendingToShare.servingAmounts,
    };
    const newPendingMealOptions = pendingMealOptions.filter(pendingMealOption => pendingMealOption.id !== mealOptionPendingId);
    const bumpedMealOptions = mealOptions.map(mealOption => ({
      ...mealOption,
      position: mealOption.position + 1,
    }));

    writeFragment(
      {
        ...editFragment,
        newMealOptionIds: newMealOptionIds.concat([optimisticMealOption.id]),
        mealOptions: bumpedMealOptions.concat([optimisticMealOption]),
        pendingMealOptions: newPendingMealOptions,
        selectedMealOptionId: optimisticMealOption.id,
      },
      context
    );

    try {
      const { data, errors } = await client.mutate<ShareMealOptionPendingMutation, ShareMealOptionPendingMutationVariables>({
        mutation: ShareMealOptionPendingDocument,
        variables: {
          input: {
            mealOptionPendingId,
          },
        },
      });

      if (errors?.length || !data) {
        throw new Error("Failed to share meal option pending");
      }

      const currentFragment = getFragment(mealTemplateId, context);
      const { id, position, name, note, servingAmounts } = data.shareMealOptionPending.mealOption;
      const replaceMealOption = { ...createMealOption(position, id), name, note, servingAmounts };
      writeFragment(
        {
          ...currentFragment,
          newMealOptionIds: currentFragment.newMealOptionIds.filter(moId => moId !== optimisticMealOption.id),
          mealOptions: currentFragment.mealOptions.filter(mo => mo.id !== optimisticMealOption.id).concat([replaceMealOption]),
          selectedMealOptionId: replaceMealOption.id,
        },
        context
      );
    } catch (error) {
      console.error("Mutation failed!!!");
      writeFragment(previousState, context);
      throw error;
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addMealOptionLocal: (src: any, { mealTemplateId, type }: MutationAddMealOptionLocalArgs, context: ResolverContext) => {
    const { getFragment, writeFragment } = getEditFragmentFuncs(type);
    const { client } = context;
    const { mealOptions, newMealOptionIds, ...editFragment } = getFragment(mealTemplateId, context);
    const nextPosition = getNextPosition(mealOptions);
    const newMealOption = createMealOption(nextPosition);

    writeFragment(
      {
        ...editFragment,
        newMealOptionIds: newMealOptionIds.concat([newMealOption.id]),
        mealOptions: mealOptions.concat([newMealOption]),
      },
      context
    );

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    client.mutate<AddMealOptionMutation, AddMealOptionMutationVariables>({
      mutation: AddMealOptionDocument,
      variables: {
        input: {
          mealTemplateId,
          mealOption: {
            name: null,
            note: null,
            position: nextPosition,
            servingAmounts: [],
          },
        },
      },
      update: (_, { data, errors }) => {
        if (errors) {
          console.error("Mutation failed!!!");
          return;
        }

        if (data) {
          const editFragment = getFragment(mealTemplateId, context);
          const { mealOptions } = editFragment;
          const { id, position } = data.addMealOption.mealOption;

          // Use the placeholder id captured when Add started (not newMealOptionIds[0]),
          // so a concurrent Share temp id is not removed instead.
          const removeMealOptionId = newMealOption.id;
          const replaceMealOption = createMealOption(position, id);
          writeFragment(
            {
              ...editFragment,
              newMealOptionIds: editFragment.newMealOptionIds.filter(moId => moId !== removeMealOptionId),
              mealOptions: mealOptions.filter(mo => mo.id !== removeMealOptionId).concat([replaceMealOption]),
              selectedMealOptionId: replaceMealOption.id,
            },
            context
          );
        }
      },
    });
  },

  removeMealOptionLocal: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    src: any,
    { mealTemplateId, type, mealOptionId }: MutationRemoveMealOptionLocalArgs,
    context: ResolverContext
  ) => {
    const { getFragment, writeFragment } = getEditFragmentFuncs(type);
    const { client } = context;
    const { mealOptions, pendingMealOptions, selectedMealOptionId, ...editFragment } = getFragment(mealTemplateId, context);
    const newMealOptions = mealOptions.filter(mo => mo.id !== mealOptionId);

    writeFragment(
      {
        ...editFragment,
        mealOptions: newMealOptions,
        pendingMealOptions,
        selectedMealOptionId:
          selectedMealOptionId === mealOptionId ? mealOptionPreviewsToSelectedId(newMealOptions, pendingMealOptions) : selectedMealOptionId,
      },
      context
    );

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    client.mutate<RemoveMealOptionMutation, RemoveMealOptionMutationVariables>({
      mutation: RemoveMealOptionDocument,
      variables: {
        input: {
          mealOptionId,
        },
      },
      update: (_, { errors }) => {
        if (errors) {
          console.error("Mutation failed!!!");
          return;
        }
      },
    });
  },

  removeMealOptionPendingLocal: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    src: any,
    { mealTemplateId, type, mealOptionPendingId }: MutationRemoveMealOptionPendingLocalArgs,
    context: ResolverContext
  ) => {
    const { getFragment, writeFragment } = getEditFragmentFuncs(type);
    const { client } = context;
    const { pendingMealOptions, mealOptions, selectedMealOptionId, ...editFragment } = getFragment(mealTemplateId, context);
    const newPendingMealOptions = pendingMealOptions.filter(pendingMealOption => pendingMealOption.id !== mealOptionPendingId);

    writeFragment(
      {
        ...editFragment,
        mealOptions,
        pendingMealOptions: newPendingMealOptions,
        selectedMealOptionId: mealOptionPreviewsToSelectedId(mealOptions, newPendingMealOptions),
      },
      context
    );

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    client.mutate<RemoveMealOptionPendingMutation, RemoveMealOptionPendingMutationVariables>({
      mutation: RemoveMealOptionPendingDocument,
      variables: {
        input: {
          mealOptionPendingId,
        },
      },
      update: (_, { errors }) => {
        if (errors) {
          console.error("Mutation failed!!!");
          return;
        }
      },
    });
  },
};
